import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { uploadDocument, UploadPayload } from 'api/documents'
import { getServerTime } from 'api/jobs'
import { isTrailApiLimitStatus } from 'api/status'
import { useRootLoaderData } from 'components/Root.loader'
import {
  DocTypeOption,
  AddFilesHandler,
  DeleteFileHandler,
} from 'components/UploadDialog/types'
import { MAX_UPLOAD_FILES, DOC_TYPE, DocType, LINK_SUPPORT } from 'config'
import { useConfirm, useAlert } from 'hooks/useDialog'
import useOcrBuilders from 'hooks/useOcrBuilders'
import { ActionKind, useReducerContext } from 'hooks/useReducerContext'
import { useCallback, useState, useEffect, useMemo } from 'react'
import { UserGroupConfig, FileImportType, Mode } from 'types'
import { nameWithOutExt } from 'utils/file'

export interface ProvidedProps {
  userGroupConfig: UserGroupConfig
  uploading: boolean
  onSend: () => Promise<void>
  onSendMobile: () => Promise<'failed' | 'trialApiLimit' | 'succeeded'>

  docTypeOptions: DocTypeOption[]
  docType: DocType | null
  onDocTypeChange: (value: string) => void

  group: string
  groupOptions: string[]
  onGroupChange: (group: string) => void
  groupDisabled: boolean

  onAddFiles: AddFilesHandler
  onDeleteFile: DeleteFileHandler
  files: MyFile[]

  ocrBuilderConfigID: number | null
  onOcrBuilderConfigIDChange: (value: number | null) => void
  importType: string
  onImportTypeChange: (type: FileImportType) => void
  disableImportType: boolean
  reset: () => void
  confirmAPILimit: () => Promise<any>
}

const ALL_TYPE_OPTIONS = [
  {
    label: '請求書',
    value: '請求書',
    description: '請求書フォーマットの帳票',
    icon: DescriptionOutlinedIcon,
  },
  {
    label: '注文書',
    value: '注文書',
    description: '注文書フォーマットの帳票',
    icon: DescriptionOutlinedIcon,
  },
  /* 非表示にしておく
  {
    label: '領収書',
    value: '領収書',
    description: '領収書タイプ（提供予定）',
    icon: ReceiptOutlinedIcon,
  },
  */
] as const

function canMultiPage(docType?: DocType) {
  return ([DOC_TYPE.INVOICE, DOC_TYPE.ORDER_FORM] as any).includes(docType)
}

const getTypeOptions = (useableDocTypes: DocType[]) =>
  ALL_TYPE_OPTIONS.map((opt) => ({
    ...opt,
    disabled: !useableDocTypes.includes(opt.value),
  }))

const getUseableDocTypes = (config: UserGroupConfig) =>
  [
    { type: DOC_TYPE.INVOICE, useable: config.useInvoiceOCR },
    {
      type: DOC_TYPE.ORDER_FORM,
      useable: config.useOrderForm,
    },
    { type: DOC_TYPE.RECEIPT, useable: false },
    { type: DOC_TYPE.OCR_BUILDER, useable: config.useOCRBuilder },
  ]
    .filter((x) => x.useable)
    .map((x) => x.type)

function isDocType(x: string): x is DocType {
  return Object.values(DOC_TYPE).includes(x as DocType)
}

let nextFileID = 1

type MyFile = File & { id: number }

function useDocUpload(mode: Mode): ProvidedProps {
  const [_, dispatch] = useReducerContext()
  const confirm = useConfirm()
  const alert = useAlert()

  const [group, setGroup] = useState('')
  const rootData = useRootLoaderData()
  const nGroups = rootData.normalGroups
  const vGroups = rootData.verifyGroups

  const groupOptions = mode === 'normal' ? nGroups : vGroups
  const [ocrBuilderConfigID, setOcrBuilderConfigID] =
    useState<ProvidedProps['ocrBuilderConfigID']>(null)

  const [files, setFiles] = useState<MyFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [importType, setImportType] = useState<FileImportType>('multi x multi')
  const userGroupConfig = rootData.userGroupConfig

  const [docType, setDocType] = useState<ProvidedProps['docType']>(null)
  const useableDocTypes = useMemo(
    () => getUseableDocTypes(userGroupConfig),
    [userGroupConfig],
  )

  const { normalOcrBuilders, verifyOcrBuilders } = useOcrBuilders()
  const ocrBuilders = mode === 'normal' ? normalOcrBuilders : verifyOcrBuilders

  const onOcrBuilderConfigIDChange = useCallback(
    (ocrBuilderConfigID: number | null) => {
      setOcrBuilderConfigID(ocrBuilderConfigID)
      const selectedOcrBuilder = ocrBuilders.find(
        (ob) => ob.ocrBuilderConfigID === ocrBuilderConfigID,
      )
      if (selectedOcrBuilder && selectedOcrBuilder.subGroup !== '')
        setGroup(selectedOcrBuilder.subGroup)
    },
    [ocrBuilders],
  )

  const groupDisabled = useMemo(
    () =>
      ocrBuilderConfigID !== null &&
      ocrBuilders.find((ob) => ob.ocrBuilderConfigID === ocrBuilderConfigID)
        ?.subGroup !== '',
    [ocrBuilderConfigID, ocrBuilders],
  )

  const onDocTypeChange = useCallback(
    (value: string) => {
      if (!isDocType(value)) {
        throw new Error(`Unknown document type. "${value}"`)
      }
      if (!canMultiPage(value)) {
        setImportType('multi x multi')
      }
      if (value !== DOC_TYPE.OCR_BUILDER) {
        onOcrBuilderConfigIDChange(null)
      } else {
        ocrBuilders[0] &&
          onOcrBuilderConfigIDChange(ocrBuilders[0].ocrBuilderConfigID)
      }
      setDocType(value)
    },
    [ocrBuilders, onOcrBuilderConfigIDChange],
  )

  const resetGroup = useCallback(
    (groupIndex = 0) => {
      if (groupOptions.length) {
        setGroup(groupOptions[groupIndex])
      }
    },
    [groupOptions],
  )

  const resetDocTypeAndGroup = useCallback(() => {
    resetGroup()
    if (useableDocTypes.length > 0) {
      onDocTypeChange(useableDocTypes[0])
    } else {
      // this means useOCR = false, useInvoice = false
      setDocType(DOC_TYPE.GENERAL_OCR)
    }
  }, [resetGroup, useableDocTypes, onDocTypeChange])

  const docTypeOptions = useMemo(
    () => getTypeOptions(useableDocTypes),
    [useableDocTypes],
  )

  const onGroupChange = useCallback(
    (group: string) => {
      setGroup(group)
    },
    [setGroup],
  )

  const onAddFiles = useCallback(async (newFiles: File[]) => {
    newFiles.forEach((f) => {
      ;(f as MyFile).id = nextFileID++
    })
    setFiles((oldFiles: MyFile[]) =>
      [...oldFiles, ...(newFiles as MyFile[])].splice(0, MAX_UPLOAD_FILES),
    )
  }, [])

  const onImportTypeChange = useCallback((type: FileImportType) => {
    setImportType(type)
  }, [])

  const uploadFile = useCallback(
    async (file: File) => {
      const name = file.name
      const ext = file.name.split('.').pop() || ''

      const data: UploadPayload = {
        file,
        docType: docType!,
        importType,
        docName: nameWithOutExt(name),
        fileExt: ext,
        subGroup: group,
      }

      if (ocrBuilderConfigID) data.ocrBuilderConfigID = ocrBuilderConfigID

      return uploadDocument(data)
    },
    [docType, group, importType, ocrBuilderConfigID],
  )

  const confirmAPILimit = useCallback(
    () =>
      confirm({
        message: [
          '一部の文書がトライアル上限値に達しました。',
          'トライアル期間中は、これ以上ご登録いただけません。',
          '継続のご利用などは担当営業へご連絡ください。',
        ],
        actionText: 'お問い合わせ',
        cancelText: '確認',
        onConfirm: () => {
          location.assign(LINK_SUPPORT)
        },
      }),
    [confirm],
  )

  const onSend = useCallback(async () => {
    setUploading(true)
    let fromDate: Date
    let openJobsBox = true
    try {
      fromDate = await getServerTime()
    } catch {
      fromDate = new Date(new Date().getTime() - 1000)
    }
    try {
      const uploadResults = await Promise.all(files.map(uploadFile))
      if (!uploadResults.every((x) => x.status === 0)) {
        alert({ message: 'ファイルアップロードに失敗しました' })
      }
    } catch (e: any) {
      if (isTrailApiLimitStatus(e.response.data?.status)) {
        // response code: 403
        confirmAPILimit()
      } else {
        alert({ message: 'ファイルアップロードに失敗しました' })
      }
      openJobsBox = false
    }
    openJobsBox &&
      dispatch({
        type: ActionKind.JOBSBOX_START,
        payload: fromDate.getTime(),
      })
    setUploading(false)
  }, [alert, confirmAPILimit, dispatch, files, uploadFile])

  const onSendMobile = useCallback(async () => {
    try {
      const uploadResults = await Promise.all(files.map(uploadFile))
      if (!uploadResults.every((x) => x.status === 0)) {
        return 'failed'
      }
    } catch (e: any) {
      if (isTrailApiLimitStatus(e.response.data?.status)) {
        // response code: 403
        return 'trialApiLimit'
      }
      return 'failed'
    }
    return 'succeeded'
  }, [files, uploadFile])

  const onDeleteFile = useCallback((idx: number) => {
    setFiles((curFiles) => curFiles.filter((_f, i) => idx !== i))
  }, [])

  const reset = useCallback(() => {
    resetDocTypeAndGroup()
    setFiles([])
    setUploading(false)
    setImportType('multi x multi')
  }, [resetDocTypeAndGroup])

  useEffect(() => {
    reset()
  }, [reset])

  const disableImportType = useMemo(
    () => (docType ? !canMultiPage(docType) : true),
    [docType],
  )

  return {
    userGroupConfig,
    uploading,
    docTypeOptions,
    docType,
    onDocTypeChange,

    group,
    groupOptions,
    onGroupChange,
    groupDisabled,

    importType,
    onImportTypeChange,
    disableImportType,

    ocrBuilderConfigID,
    onOcrBuilderConfigIDChange,

    files,
    onAddFiles,
    onDeleteFile,
    onSend,
    onSendMobile,
    reset,
    confirmAPILimit,
  }
}

export default useDocUpload
